import * as React from "react";
import { I18n } from "react-redux-i18n";
import isNil from "lodash/isNil";

import { IndividualListDto } from "@common/dto/individual/list-dto";
import { HttpErrorDto } from "@common/dto/http";
import { PedigreeGetDto } from "@common/dto/pedigree/get-dto";
import { FolderListDto } from "@common/dto/folder/list-dto";

export type ControllerProps = {
  showErrorMessage: (message?: string) => void;
  pedigreeExists: boolean;
  onSubmit: () => void;
  moveIndividualToNewPedigree: (payload: {
    folderId?: FolderListDto["id"];
    familyName: string;
    familyId: IndividualListDto['familyId'];
    individualName: string;
  }) => Promise<void>;
  moveIndividualToExistingPedigree: (payload: {
    newFamilyId?: PedigreeGetDto["id"];
    oldFamilyId: IndividualListDto['familyId'];
  }) => Promise<void>;
  individual: IndividualListDto;
};

export const useController = (props: ControllerProps) => {
  const { showErrorMessage, pedigreeExists, onSubmit, moveIndividualToNewPedigree, moveIndividualToExistingPedigree, individual } = props;

  const [loading, setLoading] = React.useState(false);
  const [folderId, setFolderId] = React.useState<FolderListDto['id']>();
  const [pedigreeName, setPedigreeName] = React.useState("");
  const [selectedPedigree, setSelectedPedigree] = React.useState<PedigreeGetDto>();


  const disableSubmitOnPedigreeExists = React.useMemo(() => !selectedPedigree?.name.trim().length, [selectedPedigree]);
  const disableSubmitOnNewPedigree = React.useMemo(() => !pedigreeName.trim().length || isNil(folderId), [pedigreeName, folderId]);
  const disableSubmitAction = (pedigreeExists ? disableSubmitOnPedigreeExists : disableSubmitOnNewPedigree) || loading;

  const handlePedigreeSelect = React.useCallback((data: { folder?: FolderListDto, pedigree: PedigreeGetDto }) => {
    setSelectedPedigree(data.pedigree);
  }, [setSelectedPedigree]);

  const handleSubmitNewPedigree = async () => {
    try {
      setLoading(true);
      await moveIndividualToNewPedigree({
        familyId: individual.familyId,
        familyName: pedigreeName,
        individualName: individual.name,
        folderId,
      });
      onSubmit();
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to move folder");

      showErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmitExistPedigree = React.useCallback(async () => {
    try {
      setLoading(true);
      await moveIndividualToExistingPedigree({
        newFamilyId: selectedPedigree?.id,
        oldFamilyId: individual.familyId
      });
      onSubmit();
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to move folder");

      showErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedPedigree, showErrorMessage, individual, onSubmit]);

  const handleSubmit = React.useCallback(() => {
    pedigreeExists ? handleSubmitExistPedigree() : handleSubmitNewPedigree();
  }, [handleSubmitExistPedigree, handleSubmitNewPedigree, pedigreeExists]);

  return {
    disableSubmitAction,
    handleSubmit,
    loading,
    setFolderId,
    setPedigreeName,
    folderId,
    pedigreeName,
    selectedPedigree,
    handlePedigreeSelect
  };
};
